// src/components/ZaraAssistant.js

import React from "react";
import AIZzra from "../assets/AI.png";
import { useLanguage } from "../context/LanguageContext";
import translations from "../data/translations";
import ZaraChatWidget from "./ZaraChatWidget";

/**
 * Two ways to use this component:
 *
 * 1) Standalone (own chat instance) — used on pages that don't already
 *    have a ZaraChatWidget elsewhere (e.g. a lone page with just this banner):
 *      <ZaraAssistant message={...} features={...} phone={phone} network={network} skipAuth />
 *
 * 2) Shared instance — used when the SAME chat drawer must be reused by
 *    other triggers on the page too (e.g. Services.js also has a floating
 *    button). Pass the `openChat` function from a single parent-level
 *    <ZaraChatWidget> so every trigger on the page opens the exact same
 *    session instead of spawning separate ones:
 *      <ZaraAssistant message={...} features={...} openChat={openChat} />
 */
const ZaraAssistant = ({ message, features = [], buttonText, phone, network, skipAuth = false, openChat: externalOpenChat }) => {
  const { language } = useLanguage();
  const t = translations[language] || translations["English"];
  const isEn = language === "English";

  const nextLabel = buttonText || t.aiChatBtn || "Start Conversation";

  const quickSuggestions = [
    { label: t.quickTopicRefund || "Refund" },
    { label: t.quickTopicComplaint || "Complaint" },
    { label: t.quickTopicFeedback || "Feedback" },
  ];

  const defaultFeatures = [
    "Quick & Easy",
    "Safe & Secure",
    "24/7 Support",
    "Trusted by Millions",
  ];

  const renderCard = (openChat) => (
    <div className="row g-0 align-items-center bg-info-subtle py-4 rounded-4">

      {/* AI Illustration */}
      <div className="col-12 col-md-3 text-center mb-3 mb-md-0">
        <img
          src={AIZzra}
          alt="AI ZARA"
          className="img-fluid"
          style={{ maxWidth: "180px" }}
        />
      </div>

      {/* Chat Bubble */}
      <div className="col-12 col-md-6 px-3">
        <div className="card shadow-sm border-0 rounded-4 p-4" style={{ background: "linear-gradient(135deg, #f8fdff 0%, #eef9ff 100%)" }}>
          <div className="d-flex align-items-center gap-2 mb-2">
            <span className="badge rounded-pill bg-info-subtle text-info fw-semibold px-3 py-2">
              {t.aiSupportBadge || "AI Support"}
            </span>
          </div>

          <p
            style={{
              fontWeight: 700,
              fontSize: "1rem",
              marginBottom: "6px",
              color: "#0f172a",
            }}
          >
            {t.aiHeroTitle || "Need help? Chat with ZARA"}
          </p>

          <p
            style={{
              fontSize: "0.85rem",
              color: "#55606e",
              lineHeight: 1.6,
              marginBottom: "12px",
            }}
          >
            {message || t.aiHeroText || "Get quick help with refunds, complaints and service requests."}
          </p>

          <button
            type="button"
            onClick={openChat}
            className="btn btn-sm fw-semibold px-3 py-2 rounded-pill d-inline-flex align-items-center gap-2 btn-info text-white shadow-sm"
            style={{ transition: "all 0.2s ease" }}
          >
            <span>{nextLabel}</span>
          </button>

          {/* <div className="d-flex flex-wrap gap-2 mt-3">
            {quickSuggestions.map((item) => (
              <button
                key={item.label}
                type="button"
                className="btn btn-sm rounded-pill border border-info-subtle text-info bg-white"
                onClick={openChat}
              >
                {item.label}
              </button>
            ))}
          </div> */}
        </div>
      </div>

      {/* Features */}
      <div className="col-12 col-md-3 mt-4 mt-md-0">
        {(features.length ? features : defaultFeatures).map((feature, index) => (
          <div
            key={index}
            className="d-flex align-items-center gap-2 mb-3"
          >
            <svg
              width="18"
              height="18"
              viewBox="0 0 24 24"
              fill="none"
              stroke="#0099CC"
              strokeWidth="2.5"
            >
              <circle cx="12" cy="12" r="10" />
              <path
                d="M9 12l2 2 4-4"
                strokeLinecap="round"
                strokeLinejoin="round"
              />
            </svg>

            <span style={{ fontSize: "0.85rem" }}>{feature}</span>
          </div>
        ))}
      </div>

    </div>
  );

  // Shared-instance mode: parent already owns a ZaraChatWidget and gave us
  // its openChat — don't spin up a second drawer, just render the card.
  if (externalOpenChat) {
    return renderCard(externalOpenChat);
  }

  // Standalone mode: this component owns its own chat drawer.
  return (
    <ZaraChatWidget isEn={isEn} initialPhone={phone} initialNetwork={network} skipAuth={skipAuth}>
      {renderCard}
    </ZaraChatWidget>
  );
};

export default ZaraAssistant;